/* eslint-disable react/prop-types */
import { useState } from "react";
import { Mail, MessageSquare, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Send Payment Link dialog.
 * Mirrors the design from the reference image:
 *  - Title: "Send Payment Link" (color6)
 *  - Subtitle: order id and amount
 *  - Two selectable option cards: "Send via Email" / "Send via SMS"
 *  - Info note: payment link will be sent to the customer's email
 *  - Footer: Cancel / Send Link buttons
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {() => void} props.onSend - Called with the selected method ("email" | "sms")
 * @param {string} props.orderLabel - e.g. "Order #31 - £5"
 * @param {string} props.email      - customer email
 * @param {string} props.phone      - customer phone
 * @param {string} props.infoNote   - info note text
 */
export default function SendPaymentLinkDialog({
  isOpen,
  onClose,
  onSend,
  orderLabel,
  email = "",
  phone = "",
  infoNote = "The payment link will be sent to the customer's email. They can complete the payment through the link.",
  isLoading = false,
}) {
  const [method, setMethod] = useState("email");

  const handleSend = () => {
    onSend?.(method);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] bg-cblack4 border-gray-800">
        <DialogHeader className="flex flex-col justify-center items-center gap-2 text-center">
          <DialogTitle className="text-color6 text-2xl font-semibold">
            Send Payment Link
          </DialogTitle>
          {orderLabel && (
            <DialogDescription className="text-cwhite3">
              {orderLabel}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-3 mt-2">
          {/* Email option */}
          <button
            type="button"
            onClick={() => setMethod("email")}
            className={cn(
              "flex items-center gap-3 w-full p-3 rounded-md border transition-colors text-left",
              method === "email"
                ? "border-cgreen bg-cblack3"
                : "border-gray-700 bg-cblack3/60 hover:border-gray-600"
            )}
          >
            <span className="h-9 w-9 rounded-md bg-cgreen/15 text-cgreen flex justify-center items-center shrink-0">
              <Mail className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-cwhite2 text-sm font-medium">Send via Email</p>
              <p className="text-cwhite3 text-xs truncate">
                {email || "No email on file"}
              </p>
            </div>
            <span
              className={cn(
                "h-4 w-4 rounded-full border-2 flex justify-center items-center shrink-0",
                method === "email" ? "border-cgreen" : "border-gray-500"
              )}
            >
              {method === "email" && (
                <span className="h-2 w-2 rounded-full bg-cgreen" />
              )}
            </span>
          </button>

          {/* SMS option */}
          <button
            type="button"
            onClick={() => setMethod("sms")}
            className={cn(
              "flex items-center gap-3 w-full p-3 rounded-md border transition-colors text-left",
              method === "sms"
                ? "border-cgreen bg-cblack3"
                : "border-gray-700 bg-cblack3/60 hover:border-gray-600"
            )}
          >
            <span className="h-9 w-9 rounded-md bg-cyellow/15 text-cyellow flex justify-center items-center shrink-0">
              <MessageSquare className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-cwhite2 text-sm font-medium">Send via SMS</p>
              <p className="text-cwhite3 text-xs truncate">
                {phone || "No phone on file"}
              </p>
            </div>
            <span
              className={cn(
                "h-4 w-4 rounded-full border-2 flex justify-center items-center shrink-0",
                method === "sms" ? "border-cgreen" : "border-gray-500"
              )}
            >
              {method === "sms" && (
                <span className="h-2 w-2 rounded-full bg-cgreen" />
              )}
            </span>
          </button>

          {/* Info note */}
          <div className="flex items-start gap-2 p-3 rounded-md border border-cyellow/40 bg-cyellow/5 mt-1">
            <Lightbulb className="h-4 w-4 text-cyellow mt-0.5 shrink-0" />
            <p className="text-cwhite2 text-xs leading-relaxed">{infoNote}</p>
          </div>
        </div>

        <DialogFooter>
          <div className="flex w-full justify-end gap-3 items-center pt-2">
            <Button
              type="button"
              variant="outline"
              className="bg-transparent border-gray-600 text-cwhite2 hover:bg-cblack3"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={isLoading}
              className="bg-cblue hover:bg-cblue/90 text-white"
              onClick={handleSend}
            >
              Send Link
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
